/**
 * AutoStep
 *
 * Generic component for automated wizard steps (3-12).
 * Shows progress, status, and allows retry on failure.
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Loader2,
  CheckCircle,
  XCircle,
  Play,
  RefreshCw,
  Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface AutoStepProps {
  title: string;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: {
    message?: string;
    [key: string]: unknown;
  };
  error?: string | null;
  onExecute: () => Promise<void>;
  onRetry?: () => Promise<void>;
  isLoading?: boolean;
  canAutoStart?: boolean;
  durationMs?: number;
  icon?: React.ReactNode;
}

export function AutoStep({
  title,
  description,
  status,
  result,
  error,
  onExecute,
  onRetry,
  isLoading,
  canAutoStart = true,
  durationMs,
  icon
}: AutoStepProps) {
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <StatusIcon status={status} isLoading={isLoading} customIcon={icon} />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pending state */}
        {status === 'pending' && !isLoading && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              This step will {canAutoStart ? 'start automatically' : 'be executed'} when ready.
            </p>
            {!canAutoStart && (
              <Button onClick={onExecute} disabled={isLoading}>
                <Play className="mr-2 h-4 w-4" />
                Start
              </Button>
            )}
          </div>
        )}

        {/* Running state */}
        {(status === 'running' || isLoading) && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm">Processing...</span>
            </div>
            <Progress value={undefined} className="h-2" />
          </div>
        )}

        {/* Completed state */}
        {status === 'completed' && result && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium">Completed</span>
              {durationMs && (
                <span className="text-xs text-muted-foreground">
                  in {formatDuration(durationMs)}
                </span>
              )}
            </div>

            {result.message && (
              <p className="text-sm text-muted-foreground">{result.message}</p>
            )}

            {/* Display result details */}
            <ResultDetails result={result} />
          </div>
        )}

        {/* Failed state */}
        {status === 'failed' && (
          <div className="space-y-4">
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error || 'Step failed'}</AlertDescription>
            </Alert>

            {onRetry && (
              <Button variant="outline" onClick={onRetry} disabled={isLoading}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatusIcon({
  status,
  isLoading,
  customIcon
}: {
  status: string;
  isLoading?: boolean;
  customIcon?: React.ReactNode;
}) {
  if (isLoading || status === 'running') {
    return <Loader2 className="h-5 w-5 animate-spin text-primary" />;
  }
  if (status === 'completed') {
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  }
  if (status === 'failed') {
    return <XCircle className="h-5 w-5 text-destructive" />;
  }
  if (customIcon) {
    return <>{customIcon}</>;
  }
  return <Clock className="h-5 w-5 text-muted-foreground" />;
}

function ResultDetails({ result }: { result: Record<string, unknown> }) {
  // Filter out internal fields
  const displayFields = Object.entries(result).filter(
    ([key]) => !['message', 'success', 'qualityScore'].includes(key)
  );

  if (displayFields.length === 0) return null;

  return (
    <div className="rounded-md bg-muted/50 p-3 space-y-1">
      {displayFields.map(([key, value]) => (
        <div key={key} className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground capitalize">
            {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}:
          </span>
          <span className="font-medium">
            {typeof value === 'number'
              ? value.toLocaleString()
              : typeof value === 'object'
                ? JSON.stringify(value)
                : String(value)}
          </span>
        </div>
      ))}
    </div>
  );
}
